<template>
  <div
    class="credit-assessment-report-preview"
    :class="{
      'is-exporting': downloadingPdf,
      'is-embedded': embedded,
      'is-single-page-view': singlePageView
    }"
  >
    <div class="preview-header">
      <h3 class="preview-section-title">Report Preview</h3>
      <div class="preview-actions">
        <button
          type="button"
          class="preview-action-btn preview-action-btn-primary"
          :disabled="downloadingPdf"
          @click="downloadPdf"
        >
          <i v-if="downloadingPdf" class="las la-spinner la-spin"></i>
          <i v-else class="las la-file-pdf"></i>
          {{ downloadingPdf ? 'Generating PDF...' : 'Download PDF' }}
        </button>
      </div>
    </div>

    <div ref="pagesContainer" class="pdf-export-content">
      <!-- Page 1: Cover -->
      <article class="report-page report-page-cover" data-report-page="1" v-show="pageVisible(1)">
        <header class="report-page-mini-header">
          <span>Kema Credit Assessment Report</span>
        </header>

        <div class="cover-body">
          <h1 class="cover-title">Kema Credit Assessment Report</h1>
          <p class="cover-subtitle">
            Merchant Name –
            <span class="cover-merchant">{{ data.merchantName }}</span>
          </p>
        </div>

        <footer class="report-page-footer">
          <span>Kema Credit Assessment Report</span>
          <span>Page 1/{{ totalPages }}</span>
        </footer>
      </article>

      <!-- Page 2: Executive Summary -->
      <article class="report-page report-page-executive-summary" data-report-page="2" v-show="pageVisible(2)">
        <header class="report-page-header">
          <span class="report-page-header-label">Kema Credit Assessment Report</span>
          <img
            src="https://kema-assets.s3.amazonaws.com/media/images/kema-logo-on-light-0.6x.png"
            alt="Kema Logo"
            class="logo-img"
            crossorigin="anonymous"
          />
        </header>

        <div class="report-banner">
          {{ data.merchantName }} CREDIT ASSESSMENT REPORT
        </div>

        <table class="report-info-table">
          <tbody>
            <tr>
              <td class="label-cell">Prepared By:</td>
              <td>{{ displayValue(data.preparedBy) }}</td>
              <td class="label-cell">Date:</td>
              <td>{{ displayValue(data.reportDate) }}</td>
            </tr>
            <tr>
              <td class="label-cell">Merchant Name:</td>
              <td>{{ displayValue(data.merchantName) }}</td>
              <td class="label-cell">Establishment Date:</td>
              <td>{{ displayValue(data.establishmentDate) }}</td>
            </tr>
            <tr>
              <td class="label-cell">Industry:</td>
              <td>{{ displayValue(data.industry) }}</td>
              <td class="label-cell">Key Shareholder(s)/ Authorized Signatory:</td>
              <td>{{ displayValue(data.keyShareholder) }}</td>
            </tr>
          </tbody>
        </table>

        <div class="report-section-banner">EXECUTIVE SUMMARY</div>

        <div class="report-summary-panel report-requirement-panel">
          <h4 class="report-subheading">{{ data.merchantName }} Requirement</h4>
          <p class="report-paragraph" v-html="formatRichText(data.companyOverview)"></p>

          <table class="report-info-table report-summary-panel-table report-requirement-table">
            <tbody>
              <tr>
                <td class="label-cell">Facility Requested</td>
                <td>{{ displayValue(data.facilityRequested) }}</td>
              </tr>
              <tr>
                <td class="label-cell">Facility Limit Requested</td>
                <td>{{ displayValue(data.facilityLimit) }}</td>
              </tr>
              <tr>
                <td class="label-cell">Tenor Requested</td>
                <td>{{ displayValue(data.tenorRequested) }}</td>
              </tr>
            </tbody>
          </table>
        </div>

        <div class="report-summary-panel report-highlights-panel">
          <h4 class="report-subheading">
            Key Assessment Highlights - {{ data.merchantName }}
          </h4>

          <table class="report-info-table report-summary-panel-table report-highlights-table">
            <tbody>
              <tr v-for="(row, index) in data.assessmentHighlights" :key="`highlight-${index}`">
                <td class="label-cell">
                  {{ row.label }}
                  <span v-if="row.subtext" class="cell-subtext">{{ row.subtext }}</span>
                </td>
                <td>{{ displayValue(row.value) }}</td>
              </tr>
            </tbody>
          </table>
        </div>

        <div class="report-footnotes">
          <p>¹ Refer to Appendix A for details on selection criteria for prime payers</p>
          <p>² Refer to Appendix E for definitions of different metrics used in this document</p>
        </div>

        <footer class="report-page-footer">
          <span>Kema Credit Assessment Report</span>
          <span>Page 2/{{ totalPages }}</span>
        </footer>
      </article>

      <!-- Page 3: Risk & Recommendation -->
      <article class="report-page" data-report-page="3" v-show="pageVisible(3)">
        <header class="report-page-header">
          <span class="report-page-header-label">Kema Credit Assessment Report</span>
          <img
            src="https://kema-assets.s3.amazonaws.com/media/images/kema-logo-on-light-0.6x.png"
            alt="Kema Logo"
            class="logo-img"
            crossorigin="anonymous"
          />
        </header>

        <table class="report-info-table report-risk-table">
          <tbody>
            <tr>
              <td class="label-cell">Risk Score (0 - 100)</td>
              <td class="risk-highlight-cell risk-score-value-cell">
                <div v-if="riskScoreGauge.hasScore" class="risk-score-gauge">
                  <div class="risk-score-gauge-ticks">
                    <span
                      v-for="(tick, index) in riskScoreGauge.ticks"
                      :key="`risk-score-tick-${index}`"
                      class="risk-score-gauge-tick-label"
                      :style="riskScoreTickStyle(tick)"
                    >{{ tick }}</span>
                  </div>
                  <div class="risk-score-gauge-scale">
                    <div
                      class="risk-score-gauge-marker"
                      :style="{
                        left: `${riskScoreGauge.markerLeftPct}%`,
                        position: 'absolute',
                        top: '6px',
                        transform: 'translateX(-50%)'
                      }"
                    >
                      <span class="risk-score-gauge-marker-arrow">▲</span>
                      <span class="risk-score-gauge-marker-value">{{ riskScoreGauge.displayScore }}</span>
                    </div>
                  </div>
                </div>
                <span v-else>—</span>
              </td>
            </tr>
            <tr>
              <td class="label-cell">Risk Grade</td>
              <td class="risk-highlight-cell risk-grade-cell">{{ displayValue(data.riskGrade) }}</td>
            </tr>
          </tbody>
        </table>

        <div class="report-strengths-weaknesses">
          <div class="sw-block">
            <div class="sw-header sw-header-strengths">Key Strengths</div>
            <ul class="sw-list">
              <li
                v-for="(item, index) in data.keyStrengths"
                :key="`strength-${index}`"
                v-html="formatRichText(item)"
              ></li>
              <li v-if="!data.keyStrengths.length" class="sw-empty">—</li>
            </ul>
          </div>

          <div class="sw-block">
            <div class="sw-header sw-header-weaknesses">Key Weaknesses</div>
            <ul class="sw-list">
              <li
                v-for="(item, index) in data.keyWeaknesses"
                :key="`weakness-${index}`"
                v-html="formatRichText(item)"
              ></li>
              <li v-if="!data.keyWeaknesses.length" class="sw-empty">—</li>
            </ul>
          </div>
        </div>

        <table class="report-info-table report-recommendation-table">
          <tbody>
            <tr>
              <td class="label-cell">Credit Recommendation</td>
              <td
                class="recommendation-value-cell"
                :class="creditRecommendationCellClass"
              >{{ displayValue(data.creditRecommendation) }}</td>
            </tr>
            <tr
              v-for="(row, index) in (data.recommendationRows || [])"
              :key="`recommendation-row-${index}`"
            >
              <td class="label-cell">
                {{ row.label }}
                <span v-if="row.subtext" class="cell-subtext">{{ row.subtext }}</span>
              </td>
              <td>{{ displayValue(row.value) }}</td>
            </tr>
          </tbody>
        </table>

        <footer class="report-page-footer">
          <span>Kema Credit Assessment Report</span>
          <span>Page 3/{{ totalPages }}</span>
        </footer>
      </article>

      <!-- Page 4: Business Profile -->
      <article class="report-page" data-report-page="4" v-show="pageVisible(4)">
        <header class="report-page-header">
          <span class="report-page-header-label">Kema Credit Assessment Report</span>
          <img
            src="https://kema-assets.s3.amazonaws.com/media/images/kema-logo-on-light-0.6x.png"
            alt="Kema Logo"
            class="logo-img"
            crossorigin="anonymous"
          />
        </header>

        <div class="report-banner">
          {{ data.merchantName }} BUSINESS PROFILE{{ businessProfilePeriodSuffix }}
        </div>

        <div class="business-profile-panel">
          <h4 class="report-subheading">{{ data.merchantName }} Overview</h4>
          <p class="report-paragraph" v-html="formatRichText(data.businessOverview)"></p>

          <table class="report-info-table business-profile-sales-table">
            <tbody>
              <tr>
                <td class="label-cell">Sales (AED) (as per VAT)</td>
                <td>{{ displayValue(data.salesAsPerVat) }}</td>
                <td class="label-cell">Net Sales (AED) (as per ERP)</td>
                <td>{{ displayValue(data.netSalesAsPerErp) }}</td>
              </tr>
            </tbody>
          </table>

          <h4 class="report-subheading sales-trend-heading">Monthly Sales Trend (In AED)</h4>

          <div v-if="chartBars.length" class="sales-chart">
            <div class="sales-chart-y-axis">
              <span
                v-for="(tick, index) in yAxisTicks"
                :key="`y-tick-${index}`"
              >{{ formatChartTick(tick) }}</span>
            </div>
            <div class="sales-chart-body">
              <div class="sales-chart-plot">
                <div class="sales-chart-grid">
                  <div
                    v-for="(tick, index) in yAxisTicks"
                    :key="`grid-${index}`"
                    class="sales-chart-grid-line"
                  ></div>
                </div>
                <div class="sales-chart-bars">
                  <div
                    v-for="(bar, index) in chartBars"
                    :key="`bar-${index}`"
                    class="sales-bar-column"
                  >
                    <div
                      class="sales-bar-fill"
                      :style="{ height: `${bar.heightPct}%` }"
                    ></div>
                  </div>
                </div>
              </div>
              <div class="sales-chart-x-labels">
                <span
                  v-for="(bar, index) in chartBars"
                  :key="`label-${index}`"
                  class="sales-bar-label"
                >{{ bar.label }}</span>
              </div>
            </div>
          </div>
          <div v-else class="sales-chart-empty">—</div>

          <ul class="sales-metrics-list">
            <li
              v-for="(metric, index) in salesMetrics"
              :key="`metric-${index}`"
              class="sales-metric-item"
            >
              <span class="sales-metric-check" aria-hidden="true">✓</span>
              <span>
                <strong>{{ metric.label }}</strong>
                <span v-if="metric.value !== '—'"> - {{ metric.value }}</span>
              </span>
            </li>
          </ul>

          <ul class="conclusion-list sales-analysis-list">
            <li
              v-for="(item, index) in revenueNotes"
              :key="`revenue-note-${index}`"
              v-html="formatRichText(item)"
            ></li>
            <li v-if="!revenueNotes.length" class="conclusion-empty">—</li>
          </ul>
        </div>

        <footer class="report-page-footer">
          <span>Kema Credit Assessment Report</span>
          <span>Page 4/{{ totalPages }}</span>
        </footer>
      </article>

      <!-- Page 5: Three-year Sales Trend -->
      <article class="report-page" data-report-page="5" v-show="pageVisible(5)">
        <header class="report-page-header">
          <span class="report-page-header-label">Kema Credit Assessment Report</span>
          <img
            src="https://kema-assets.s3.amazonaws.com/media/images/kema-logo-on-light-0.6x.png"
            alt="Kema Logo"
            class="logo-img"
            crossorigin="anonymous"
          />
        </header>

        <div class="yearly-sales-panel">
          <h4 class="yearly-sales-title">Three-year Sales Trend (In mAED)</h4>

          <div v-if="yearlySalesChart.hasData" class="yearly-sales-chart">
            <div class="yearly-sales-plot-row">
              <div class="yearly-sales-y-axis">
                <span
                  v-for="(tick, index) in yearlySalesChart.yTicks"
                  :key="`yearly-y-${index}`"
                >{{ yearlySalesChart.formatTick(tick) }}</span>
              </div>
              <div class="yearly-sales-plot">
                <div class="yearly-sales-grid">
                  <div
                    v-for="(tick, index) in yearlySalesChart.yTicks"
                    :key="`yearly-grid-${index}`"
                    class="yearly-sales-grid-line"
                  ></div>
                </div>
                <svg
                  class="yearly-sales-trend-svg"
                  :viewBox="`0 0 ${yearlySalesChart.plotWidth} ${yearlySalesChart.plotHeight}`"
                  preserveAspectRatio="none"
                >
                  <path
                    v-if="yearlySalesChart.trendPath"
                    class="yearly-sales-trend-line"
                    :d="yearlySalesChart.trendPath"
                  />
                </svg>
                <div class="yearly-sales-bars">
                  <div
                    v-for="(bar, index) in yearlySalesChart.bars"
                    :key="`yearly-bar-${index}`"
                    class="yearly-sales-column"
                  >
                    <div
                      class="yearly-sales-bar-fill"
                      :style="{ height: `${bar.heightPct}%` }"
                    >
                      <span class="yearly-sales-bar-value">{{ bar.displayValue }}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div class="yearly-sales-x-labels">
              <span class="yearly-sales-y-spacer" aria-hidden="true"></span>
              <span
                v-for="(bar, index) in yearlySalesChart.bars"
                :key="`yearly-label-${index}`"
                class="yearly-sales-x-label"
              >{{ bar.label }}</span>
            </div>
          </div>
          <div v-else class="sales-chart-empty">—</div>

          <ul class="conclusion-list yearly-sales-notes">
            <li
              v-for="(item, index) in yearlySalesNotes"
              :key="`yearly-note-${index}`"
              v-html="formatRichText(item)"
            ></li>
            <li v-if="!yearlySalesNotes.length" class="conclusion-empty">—</li>
          </ul>
        </div>

        <footer class="report-page-footer">
          <span>Kema Credit Assessment Report</span>
          <span>Page 5/{{ totalPages }}</span>
        </footer>
      </article>

      <!-- Page 6: Financial Analysis -->
      <article class="report-page" data-report-page="6" v-show="pageVisible(6)">
        <header class="report-page-header">
          <span class="report-page-header-label">Kema Credit Assessment Report</span>
          <img
            src="https://kema-assets.s3.amazonaws.com/media/images/kema-logo-on-light-0.6x.png"
            alt="Kema Logo"
            class="logo-img"
            crossorigin="anonymous"
          />
        </header>

        <div class="report-banner report-banner-left">
          {{ data.merchantName }} FINANCIAL ANALYSIS
        </div>

        <div class="financial-analysis-panel">
          <h4 class="report-subheading">Cash Ratio &amp; Working Capital Ratio Trends</h4>
          <div class="financial-ratio-heading-spacer" aria-hidden="true"></div>

          <div v-if="ratioTrendChart.hasData" class="ratio-trend-chart">
            <div class="ratio-chart-y-axis-wrap ratio-chart-y-axis-wrap-left">
              <div class="ratio-axis-title-stack">
                <span>Cash</span>
                <span>Ratio</span>
              </div>
              <div class="ratio-chart-y-axis ratio-chart-y-axis-left">
                <span
                  v-for="(tick, index) in ratioTrendChart.cashYTicks"
                  :key="`cash-y-${index}`"
                >{{ formatRatioTick(tick) }}</span>
              </div>
            </div>

            <div class="ratio-chart-main">
              <svg
                class="ratio-chart-svg"
                :viewBox="`0 0 ${ratioTrendChart.plotWidth} ${ratioTrendChart.plotHeight}`"
                :width="ratioTrendChart.plotWidth"
                :height="ratioTrendChart.plotHeight"
                preserveAspectRatio="xMidYMid meet"
              >
                <g class="ratio-chart-grid-lines">
                  <line
                    v-for="(tick, index) in ratioTrendChart.cashYTicks"
                    :key="`cash-grid-${index}`"
                    :x1="0"
                    :y1="ratioTrendChart.yCash(tick)"
                    :x2="ratioTrendChart.plotWidth"
                    :y2="ratioTrendChart.yCash(tick)"
                  />
                </g>
                <path
                  v-if="ratioTrendChart.wcrPath"
                  class="ratio-line ratio-line-wcr"
                  :d="ratioTrendChart.wcrPath"
                />
                <path
                  v-if="ratioTrendChart.cashPath"
                  class="ratio-line ratio-line-cash"
                  :d="ratioTrendChart.cashPath"
                />
              </svg>

              <div class="ratio-chart-x-labels">
                <span
                  v-for="(label, index) in ratioTrendChart.labels"
                  :key="`ratio-label-${index}`"
                  class="ratio-chart-x-label"
                >{{ label }}</span>
              </div>

              <div class="ratio-chart-legend">
                <span class="ratio-legend-item">
                  <span class="ratio-legend-swatch ratio-legend-swatch-wcr"></span>
                  Working Capital Ratio
                </span>
                <span class="ratio-legend-item">
                  <span class="ratio-legend-swatch ratio-legend-swatch-cash"></span>
                  Cash Ratio
                </span>
              </div>
            </div>

            <div class="ratio-chart-y-axis-wrap ratio-chart-y-axis-wrap-right">
              <div class="ratio-chart-y-axis ratio-chart-y-axis-right">
                <span
                  v-for="(tick, index) in ratioTrendChart.wcrYTicks"
                  :key="`wcr-y-${index}`"
                >{{ formatRatioTick(tick) }}</span>
              </div>
              <div class="ratio-axis-title-stack">
                <span>Working</span>
                <span>Capital</span>
                <span>Ratio</span>
              </div>
            </div>
          </div>
          <div v-else class="sales-chart-empty">—</div>

          <ul class="conclusion-list financial-analysis-list">
            <li
              v-for="(item, index) in data.cashQuickRatioPoints"
              :key="`cash-quick-ratio-${index}`"
              v-html="formatRichText(item)"
            ></li>
            <li v-if="!data.cashQuickRatioPoints.length" class="conclusion-empty">—</li>
          </ul>
        </div>

        <footer class="report-page-footer">
          <span>Kema Credit Assessment Report</span>
          <span>Page 6/{{ totalPages }}</span>
        </footer>
      </article>

      <!-- Page 7: Loans & Liabilities Composition -->
      <article class="report-page" data-report-page="7" v-show="pageVisible(7)">
        <header class="report-page-header">
          <span class="report-page-header-label">Kema Credit Assessment Report</span>
          <img
            src="https://kema-assets.s3.amazonaws.com/media/images/kema-logo-on-light-0.6x.png"
            alt="Kema Logo"
            class="logo-img"
            crossorigin="anonymous"
          />
        </header>

        <div class="loans-liabilities-panel">
          <h4 class="loans-liabilities-title">Loans &amp; Liabilities Composition (In mAED)</h4>
        </div>

        <footer class="report-page-footer">
          <span>Kema Credit Assessment Report</span>
          <span>Page 7/{{ totalPages }}</span>
        </footer>
      </article>

      <!-- Page 8: Payers & Receivables Analysis -->
      <article class="report-page" data-report-page="8" v-show="pageVisible(8)">
        <header class="report-page-header">
          <span class="report-page-header-label">Kema Credit Assessment Report</span>
          <img
            src="https://kema-assets.s3.amazonaws.com/media/images/kema-logo-on-light-0.6x.png"
            alt="Kema Logo"
            class="logo-img"
            crossorigin="anonymous"
          />
        </header>

        <div class="report-banner report-banner-left">
          {{ data.merchantName }} PAYERS &amp; RECEIVABLES ANALYSIS
        </div>

        <div class="payers-analysis-panel">
          <h4 class="report-subheading">
            {{ data.merchantName }} Top - 5 Payer Sales &amp; Industry Concentration Analysis
          </h4>

          <div v-if="payerConcentrationChart.hasData" class="payer-concentration-chart">
            <PayerBarChart
              :chart="payerConcentrationChart"
              show-sector
            />
          </div>
          <div v-else class="sales-chart-empty">—</div>

          <ul class="conclusion-list payers-analysis-list">
            <li
              v-for="(item, index) in data.payerConcentrationAnalysis"
              :key="`payer-analysis-${index}`"
              v-html="formatRichText(item)"
            ></li>
            <li v-if="!data.payerConcentrationAnalysis.length" class="conclusion-empty">—</li>
          </ul>

          <section class="payers-page8-overview-section">
            <h4 class="report-subheading">
              {{ data.merchantName }} Prime Payers Overview
            </h4>

            <ul class="conclusion-list payers-analysis-list">
              <li
                v-for="(item, index) in data.primePayersOverview"
                :key="`prime-payers-overview-${index}`"
                v-html="formatRichText(item)"
              ></li>
              <li v-if="!data.primePayersOverview.length" class="conclusion-empty">—</li>
            </ul>
          </section>
        </div>

        <div class="report-footnotes">
          <p>⁴ Refer to Appendix C for overview of prime payers in {{ data.merchantName }} portfolio</p>
          <p>⁵ Refer to Appendix A for details on selection criteria for prime payers</p>
        </div>

        <footer class="report-page-footer">
          <span>Kema Credit Assessment Report</span>
          <span>Page 8/{{ totalPages }}</span>
        </footer>
      </article>

      <!-- Page 9: Prime & Kema Approved Payer Metrics -->
      <article class="report-page report-page-payer-metrics" data-report-page="9" v-show="pageVisible(9)">
        <header class="report-page-header">
          <span class="report-page-header-label">Kema Credit Assessment Report</span>
          <img
            src="https://kema-assets.s3.amazonaws.com/media/images/kema-logo-on-light-0.6x.png"
            alt="Kema Logo"
            class="logo-img"
            crossorigin="anonymous"
          />
        </header>

        <section class="payer-metrics-section">
          <div class="payer-metrics-text-box">
            <ul class="conclusion-list payer-metrics-intro-list">
              <li v-html="formatRichText('Below is a summary of key metrics of the **Prime Payers**:')"></li>
            </ul>
          </div>

          <table class="report-info-table payer-metrics-table">
            <thead>
              <tr>
                <th>Key Assessment Metrics - Prime Payers</th>
                <th>Recent Value (last 12 months)</th>
                <th>Lifetime Value (last 8 years)</th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="(row, index) in primePayerMetricRows"
                :key="`prime-payer-metric-${index}`"
              >
                <td class="label-cell">{{ row.label }}</td>
                <td>{{ row.recent }}</td>
                <td>{{ row.lifetime }}</td>
              </tr>
            </tbody>
          </table>

          <p
            v-if="data.primePayerPcrFootnote"
            class="payer-metrics-footnote"
            v-html="formatRichText(data.primePayerPcrFootnote)"
          ></p>
        </section>

        <section class="payer-metrics-section payer-metrics-section-spaced">
          <div class="payer-metrics-text-box">
            <h4 class="report-subheading payer-metrics-overview-heading">
              {{ data.merchantName }} Kema Approved Payers Overview
            </h4>

            <ul class="conclusion-list payer-metrics-overview-list">
              <li
                v-for="(item, index) in data.approvedPayersOverview"
                :key="`approved-payers-overview-${index}`"
                v-html="formatRichText(item)"
              ></li>
              <li v-if="!data.approvedPayersOverview.length" class="conclusion-empty">—</li>
            </ul>
          </div>

          <table class="report-info-table payer-metrics-table">
            <thead>
              <tr>
                <th>Key Assessment Metrics - Kema Approved Payers</th>
                <th>Recent Value (last 12 months)</th>
                <th>Lifetime Value (last 8 years)</th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="(row, index) in kemaApprovedPayerMetricRows"
                :key="`kema-approved-metric-${index}`"
              >
                <td class="label-cell">{{ row.label }}</td>
                <td>{{ row.recent }}</td>
                <td>{{ row.lifetime }}</td>
              </tr>
            </tbody>
          </table>
        </section>

        <div class="report-footnotes">
          <p>⁶ Refer to Appendix D for complete list of names of Kema approved payers</p>
        </div>

        <footer class="report-page-footer">
          <span>Kema Credit Assessment Report</span>
          <span>Page 9/{{ totalPages }}</span>
        </footer>
      </article>

      <!-- Page 10: Receivables Ageing & Payer Concentration -->
      <article class="report-page" data-report-page="10" v-show="pageVisible(10)">
        <header class="report-page-header">
          <span class="report-page-header-label">Kema Credit Assessment Report</span>
          <img
            src="https://kema-assets.s3.amazonaws.com/media/images/kema-logo-on-light-0.6x.png"
            alt="Kema Logo"
            class="logo-img"
            crossorigin="anonymous"
          />
        </header>

        <div class="receivables-page10-panel">
          <section class="receivables-page10-section">
            <h4 class="report-subheading">Receivables Ageing Analysis</h4>

            <div v-if="agingBucketChart.hasData" class="payer-concentration-chart">
              <PayerBarChart
                :chart="agingBucketChart"
                label-width="narrow"
                bar-color="blue"
                x-tick-type="currency"
              />
            </div>
            <div v-else class="sales-chart-empty">—</div>

            <ul class="conclusion-list payers-analysis-list">
              <li
                v-for="(item, index) in data.agingBucketAnalysis"
                :key="`aging-analysis-${index}`"
                v-html="formatRichText(item)"
              ></li>
              <li v-if="!data.agingBucketAnalysis.length" class="conclusion-empty">—</li>
            </ul>
          </section>

          <section class="receivables-page10-section">
            <h4 class="report-subheading">Receivables Payer Concentration Analysis</h4>

            <div v-if="recOutstandingChart.hasData" class="payer-concentration-chart">
              <PayerBarChart
                :chart="recOutstandingChart"
                label-width="narrow"
              />
            </div>
            <div v-else class="sales-chart-empty">—</div>

            <ul class="conclusion-list payers-analysis-list">
              <li
                v-for="(item, index) in data.recOutstandingAnalysis"
                :key="`rec-analysis-${index}`"
                v-html="formatRichText(item)"
              ></li>
              <li v-if="!data.recOutstandingAnalysis.length" class="conclusion-empty">—</li>
            </ul>
          </section>
        </div>

        <footer class="report-page-footer">
          <span>Kema Credit Assessment Report</span>
          <span>Page 10/{{ totalPages }}</span>
        </footer>
      </article>

      <!-- Page 11: Risk Scoring Overview -->
      <article class="report-page report-page-risk-scoring" data-report-page="11" v-show="pageVisible(11)">
        <header class="report-page-header">
          <span class="report-page-header-label">Kema Credit Assessment Report</span>
          <img
            src="https://kema-assets.s3.amazonaws.com/media/images/kema-logo-on-light-0.6x.png"
            alt="Kema Logo"
            class="logo-img"
            crossorigin="anonymous"
          />
        </header>

        <div class="report-banner report-banner-left">
          {{ data.merchantName }} RISK SCORING OVERVIEW
        </div>

        <table class="report-info-table risk-scoring-table">
          <thead>
            <tr>
              <th>Assessed Risk</th>
              <th>Obtained Score</th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="(row, index) in riskScoringRows"
              :key="`risk-scoring-row-${index}`"
            >
              <td class="label-cell">{{ row.label }}</td>
              <td>{{ row.scoreDisplay }}</td>
            </tr>
            <tr>
              <td class="label-cell">Overall Score</td>
              <td class="risk-highlight-cell">{{ riskOverallScoreDisplay }}</td>
            </tr>
            <tr>
              <td class="label-cell">Overall Risk Grade</td>
              <td class="risk-highlight-cell">{{ displayValue(data.riskGrade) }}</td>
            </tr>
          </tbody>
        </table>

        <div class="risk-scoring-panel">
          <h4 class="report-subheading">Relative Risk Performance</h4>

          <div v-if="riskPerformanceChart.hasData" class="risk-performance-chart">
            <PayerBarChart
              :chart="riskPerformanceChart"
              label-width="risk"
              bar-color="blue"
              show-bar-values
            />
          </div>
          <div v-else class="sales-chart-empty">—</div>

          <h4 class="report-subheading risk-scoring-strengths-heading">
            {{ data.merchantName }} Strengths:
          </h4>
          <ul class="conclusion-list">
            <li
              v-for="(item, index) in data.relativeRiskStrengths"
              :key="`relative-risk-strength-${index}`"
              v-html="formatRichText(item)"
            ></li>
            <li v-if="!data.relativeRiskStrengths.length" class="conclusion-empty">—</li>
          </ul>

          <h4 class="report-subheading risk-scoring-strengths-heading">
            {{ data.merchantName }} Weaknesses:
          </h4>
          <ul class="conclusion-list">
            <li
              v-for="(item, index) in data.relativeRiskWeaknesses"
              :key="`relative-risk-weakness-${index}`"
              v-html="formatRichText(item)"
            ></li>
            <li v-if="!data.relativeRiskWeaknesses.length" class="conclusion-empty">—</li>
          </ul>
        </div>

        <footer class="report-page-footer">
          <span>Kema Credit Assessment Report</span>
          <span>Page 11/{{ totalPages }}</span>
        </footer>
      </article>

      <!-- Page 12: Conclusion & Recommendation -->
      <article class="report-page" data-report-page="12" v-show="pageVisible(12)">
        <header class="report-page-header">
          <span class="report-page-header-label">Kema Credit Assessment Report</span>
          <img
            src="https://kema-assets.s3.amazonaws.com/media/images/kema-logo-on-light-0.6x.png"
            alt="Kema Logo"
            class="logo-img"
            crossorigin="anonymous"
          />
        </header>

        <div class="report-banner report-banner-left">
          CONCLUSION &amp; RECOMMENDATION
        </div>

        <div class="conclusion-panel">
          <h4 class="conclusion-section-heading">Conclusion:</h4>
          <ul class="conclusion-list">
            <li
              v-for="(item, index) in data.conclusionPoints"
              :key="`conclusion-${index}`"
              v-html="formatRichText(item)"
            ></li>
            <li v-if="!data.conclusionPoints.length" class="conclusion-empty">—</li>
          </ul>

          <h4 class="conclusion-section-heading">Recommendation(s):</h4>
          <ul class="conclusion-list">
            <li
              v-for="(item, index) in data.recommendationPoints"
              :key="`recommendation-${index}`"
              v-html="formatRichText(item)"
            ></li>
            <li v-if="!data.recommendationPoints.length" class="conclusion-empty">—</li>
          </ul>
        </div>

        <footer class="report-page-footer">
          <span>Kema Credit Assessment Report</span>
          <span>Page 12/{{ totalPages }}</span>
        </footer>
      </article>

      <!-- Page 13: Appendix A - Prime Payer Criteria (static) -->
      <article class="report-page" data-report-page="13" v-show="pageVisible(13)">
        <header class="report-page-header">
          <span class="report-page-header-label">Kema Credit Assessment Report</span>
          <img
            src="https://kema-assets.s3.amazonaws.com/media/images/kema-logo-on-light-0.6x.png"
            alt="Kema Logo"
            class="logo-img"
            crossorigin="anonymous"
          />
        </header>

        <div class="report-banner report-banner-left">
          APPENDIX A - Prime Payer Criteria
        </div>

        <div class="appendix-panel">
          <ul class="appendix-list">
            <li
              v-for="(item, index) in primePayerCriteria"
              :key="`prime-payer-criterion-${index}`"
            >
              {{ item }}
            </li>
          </ul>
        </div>

        <footer class="report-page-footer">
          <span>Kema Credit Assessment Report</span>
          <span>Page 13/{{ totalPages }}</span>
        </footer>
      </article>

      <!-- Page 14: Appendix B - Loans & Liabilities -->
      <article class="report-page" data-report-page="14" v-show="pageVisible(14)">
        <header class="report-page-header">
          <span class="report-page-header-label">Kema Credit Assessment Report</span>
          <img
            src="https://kema-assets.s3.amazonaws.com/media/images/kema-logo-on-light-0.6x.png"
            alt="Kema Logo"
            class="logo-img"
            crossorigin="anonymous"
          />
        </header>

        <div class="report-banner report-banner-left">
          APPENDIX B - {{ data.merchantName }} Loans &amp; Liabilities - {{ data.debtsTotal }}
        </div>

        <table class="report-info-table debts-table">
          <thead>
            <tr>
              <th>Financing Details</th>
              <th>Approval Amount (AED)</th>
              <th>Repayment amount (AED - per month)</th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="(debt, index) in data.debts"
              :key="`debt-${index}`"
            >
              <td>{{ displayValue(debt.lender) }}</td>
              <td>{{ displayValue(debt.approvalAmount) }}</td>
              <td>{{ displayValue(debt.repaymentAmount) }}</td>
            </tr>
            <tr v-if="!data.debts || !data.debts.length">
              <td colspan="3">—</td>
            </tr>
          </tbody>
        </table>

        <footer class="report-page-footer">
          <span>Kema Credit Assessment Report</span>
          <span>Page 14/{{ totalPages }}</span>
        </footer>
      </article>

      <!-- Page 15: Appendix C - Prime Payers -->
      <article class="report-page" data-report-page="15" v-show="pageVisible(15)">
        <header class="report-page-header">
          <span class="report-page-header-label">Kema Credit Assessment Report</span>
          <img
            src="https://kema-assets.s3.amazonaws.com/media/images/kema-logo-on-light-0.6x.png"
            alt="Kema Logo"
            class="logo-img"
            crossorigin="anonymous"
          />
        </header>

        <div class="report-banner report-banner-left">
          APPENDIX C - {{ data.merchantName }} Prime Payers
        </div>

        <table class="report-info-table prime-payers-table">
          <thead>
            <tr>
              <th>Company Name</th>
              <th class="prime-payers-rating-header">Moody's Rating</th>
              <th>Parent Company</th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="(payer, index) in data.primePayers"
              :key="`prime-payer-${index}`"
            >
              <td>{{ displayValue(payer.name) }}</td>
              <td class="prime-payers-rating-cell">{{ displayValue(payer.moodyRating) }}</td>
              <td>{{ displayValue(payer.parent) }}</td>
            </tr>
            <tr v-if="!data.primePayers || !data.primePayers.length">
              <td colspan="3">—</td>
            </tr>
          </tbody>
        </table>

        <footer class="report-page-footer">
          <span>Kema Credit Assessment Report</span>
          <span>Page 15/{{ totalPages }}</span>
        </footer>
      </article>

      <!-- Page 16: Appendix D - Kema Approved Payers -->
      <article class="report-page" data-report-page="16" v-show="pageVisible(16)">
        <header class="report-page-header">
          <span class="report-page-header-label">Kema Credit Assessment Report</span>
          <img
            src="https://kema-assets.s3.amazonaws.com/media/images/kema-logo-on-light-0.6x.png"
            alt="Kema Logo"
            class="logo-img"
            crossorigin="anonymous"
          />
        </header>

        <div class="report-banner report-banner-left">
          APPENDIX D - {{ data.merchantName }} Kema Approved Payers
        </div>

        <div class="appendix-panel">
          <ul class="appendix-list">
            <li
              v-for="(name, index) in data.approvedPayers"
              :key="`approved-payer-${index}`"
            >{{ name }}</li>
            <li v-if="!data.approvedPayers.length">—</li>
          </ul>
        </div>

        <footer class="report-page-footer">
          <span>Kema Credit Assessment Report</span>
          <span>Page 16/{{ totalPages }}</span>
        </footer>
      </article>

      <!-- Page 17: Appendix E - Metric Definitions (static) -->
      <article class="report-page" data-report-page="17" v-show="pageVisible(17)">
        <header class="report-page-header">
          <span class="report-page-header-label">Kema Credit Assessment Report</span>
          <img
            src="https://kema-assets.s3.amazonaws.com/media/images/kema-logo-on-light-0.6x.png"
            alt="Kema Logo"
            class="logo-img"
            crossorigin="anonymous"
          />
        </header>

        <div class="report-banner report-banner-left">
          APPENDIX E - Metric Definitions
        </div>

        <table class="report-info-table appendix-metrics-table">
          <thead>
            <tr>
              <th>Metric Names</th>
              <th>Metric Definitions</th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="(row, index) in metricDefinitions"
              :key="`metric-definition-${index}`"
            >
              <td class="label-cell">{{ row.name }}</td>
              <td>{{ row.definition }}</td>
            </tr>
          </tbody>
        </table>

        <footer class="report-page-footer">
          <span>Kema Credit Assessment Report</span>
          <span>Page 17/{{ totalPages }}</span>
        </footer>
      </article>
    </div>
  </div>
</template>

<script>
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import PayerBarChart from './creditReport/PayerBarChart.vue';
import { REPORT_PREVIEW_PAGE_COUNT } from '../constants/reportEditorPages.js';

const PAGE_WIDTH_PX = 816;
const PAGE_HEIGHT_PX = 1056;
const PDF_WIDTH_IN = 8.5;
const PDF_HEIGHT_IN = 11;

const PRIME_PAYER_CRITERIA = [
  'Corporate structure (preferably listed or part of a large group)',
  'Revenue > USD 300M',
  'Operating history >10 years',
  'Employee strength >700',
  'Presence in multiple countries',
  'No widely reported regulatory / financial controversies'
];

const RISK_SUMMARY_CATEGORIES = [
  { key: 'Operations', label: 'Operations Risk' },
  { key: 'Liquidity', label: 'Liquidity Risk' },
  { key: 'Mgmt, Bus & Ind.', label: 'Management, business, & Industrial Risk' },
  { key: 'Third Party', label: 'Repayment Risk' }
];

const METRIC_DEFINITIONS = [
  {
    name: 'LTM Net Sales',
    definition: 'Sales booked in last 12 months net of any credit notes'
  },
  {
    name: 'LTM Gross Sales',
    definition: 'Sales booked in last 12 months gross of any credit notes'
  },
  {
    name: 'Days Sales Outstanding (DSO)',
    definition: 'The average time taken to collect payments from customers, weighted by invoice amount.'
  },
  {
    name: 'Cash Coverage Ratio',
    definition: 'The ratio of 12-month average bank balance to monthly financial liabilities (includes repayment commitments for term loans and interest expense for revolving facilities)'
  },
  {
    name: 'Active Customers',
    definition: 'The number of customers who have transacted within last 12 months'
  },
  {
    name: 'Payment Completion Ratio (PCR)',
    definition: 'The percentage of paid invoices relative to total invoices (paid + overdue).'
  },
  {
    name: 'PCR % <= 60 days overdue',
    definition: 'The percentage of payments completed within 60 days of their due date.'
  },
  {
    name: 'Working Capital Ratio(WCR)',
    definition: 'Measures short-term financial health by comparing available assets, i.e., accounts receivable + bank balance to accounts payable + total outstanding liabilities'
  },
  {
    name: 'Cash Ratio',
    definition: 'Measures liquidity by comparing the month-end bank balance to monthly accounts payable + outstanding liabilities, assessing the ability to cover short-term liabilities using only cash reserves.'
  },
  {
    name: 'ADTP',
    definition: 'The weighted average number of days the payer takes to make invoice payments. The number of days is computed as a difference of the payment date and the due date of the invoice and weighted by the invoice amounts.'
  }
];

export default {
  name: 'CreditAssessmentReportPreview',
  components: {
    PayerBarChart
  },
  props: {
    data: {
      type: Object,
      required: true
    },
    embedded: {
      type: Boolean,
      default: false
    },
    singlePageView: {
      type: Boolean,
      default: false
    },
    visiblePage: {
      type: Number,
      default: 1
    }
  },
  data() {
    return {
      downloadingPdf: false,
      totalPages: REPORT_PREVIEW_PAGE_COUNT,
      primePayerCriteria: PRIME_PAYER_CRITERIA,
      metricDefinitions: METRIC_DEFINITIONS
    };
  },
  computed: {
    pdfFilename() {
      const name = (this.data.merchantName || 'credit_assessment')
        .replace(/[^\w\s-]/g, '')
        .trim()
        .replace(/\s+/g, '_');
      return `${name}_Credit_Assessment_Report.pdf`;
    },
    businessProfilePeriodSuffix() {
      const period = this.data.businessProfilePeriod;
      if (!period || period === '—') return '';
      return ` - ${String(period).toUpperCase()}`;
    },
    creditRecommendationCellClass() {
      const value = String(this.data.creditRecommendation || '').trim().toUpperCase();
      if (value === 'REJECTED' || value === 'REJECT' || value === 'DECLINE') {
        return 'rejected-cell';
      }
      return 'approved-cell';
    },
    chartBars() {
      const trend = Array.isArray(this.data.monthlySalesTrend)
        ? this.data.monthlySalesTrend
        : [];

      if (!trend.length) return [];

      const values = trend.map((item) => Number(item.value) || 0);
      const maxValue = Math.max(...values, 1);
      const yMax = Math.max(Math.ceil(maxValue / 500000) * 500000, 500000);

      return trend.map((item) => ({
        label: item.label || '—',
        heightPct: Math.round(((Number(item.value) || 0) / yMax) * 100)
      }));
    },
    yAxisTicks() {
      const trend = Array.isArray(this.data.monthlySalesTrend)
        ? this.data.monthlySalesTrend
        : [];
      const values = trend.map((item) => Number(item.value) || 0);
      const maxValue = Math.max(...values, 1);
      const yMax = Math.max(Math.ceil(maxValue / 500000) * 500000, 500000);

      return [yMax, Math.round((yMax * 2) / 3), Math.round(yMax / 3), 0];
    },
    salesMetrics() {
      return [
        {
          label: 'Average Sales (monthly)',
          value: this.displayValue(this.data.averageMonthlySales)
        },
        {
          label: 'Min Sales',
          value: this.displayValue(this.data.minSales)
        },
        {
          label: 'Max Sales',
          value: this.displayValue(this.data.maxSales)
        }
      ];
    },
    revenueNotes() {
      const notes = this.data.revenueNotes;
      if (Array.isArray(notes)) {
        return notes.filter((item) => item !== undefined && item !== null && String(item).trim());
      }
      if (typeof notes === 'string' && notes.trim()) {
        return [notes.trim()];
      }
      return [];
    },
    yearlySalesNotes() {
      const notes = this.data.yearlySalesNotes;
      if (Array.isArray(notes)) {
        return notes.filter((item) => item !== undefined && item !== null && String(item).trim());
      }
      if (typeof notes === 'string' && notes.trim()) {
        return [notes.trim()];
      }
      return [];
    },
    yearlySalesChart() {
      const trend = Array.isArray(this.data.yearlySalesTrend)
        ? this.data.yearlySalesTrend
        : [];

      if (!trend.length) {
        return { hasData: false };
      }

      const values = trend.map((item) => Number(item.value) || 0);
      const yMax = this.buildRatioAxisMax(values, 10);
      const yTicks = this.buildRatioAxisTicks(yMax, 5);
      const plotWidth = 540;
      const plotHeight = 210;
      const pointCount = trend.length;
      const xAt = (index) => (
        pointCount === 1
          ? plotWidth / 2
          : ((index + 0.5) / pointCount) * plotWidth
      );
      const yAt = (value) => plotHeight - ((Number(value) || 0) / yMax) * plotHeight;
      const points = trend.map((item, index) => ({
        x: xAt(index),
        y: yAt(item.value)
      }));

      return {
        hasData: true,
        plotWidth,
        plotHeight,
        yTicks,
        trendPath: this.buildLinePath(points),
        formatTick: (tick) => {
          const number = Number(tick);
          if (!Number.isFinite(number)) return '0';
          return Number.isInteger(number) ? String(number) : String(number);
        },
        bars: trend.map((item) => {
          const value = Number(item.value) || 0;
          return {
            label: item.label || '—',
            value,
            heightPct: yMax ? (value / yMax) * 100 : 0,
            displayValue: Number.isInteger(value) ? String(value) : String(value)
          };
        })
      };
    },
    ratioTrendChart() {
      const cashTrend = Array.isArray(this.data.cashRatioTrend)
        ? this.data.cashRatioTrend
        : [];
      const wcrTrend = Array.isArray(this.data.workingCapitalRatioTrend)
        ? this.data.workingCapitalRatioTrend
        : [];
      const pointCount = Math.max(cashTrend.length, wcrTrend.length);

      if (!pointCount) {
        return { hasData: false };
      }

      const cashValues = cashTrend.map((point) => Number(point.value) || 0);
      const wcrValues = wcrTrend.map((point) => Number(point.value) || 0);
      const cashMax = this.buildRatioAxisMax(cashValues, 1);
      const wcrMax = this.buildRatioAxisMax(wcrValues, 1);
      const plotWidth = 620;
      const plotHeight = 150;
      const labels = [];

      for (let index = 0; index < pointCount; index += 1) {
        labels.push(cashTrend[index]?.label || wcrTrend[index]?.label || '—');
      }

      const xAt = (index) => (
        pointCount === 1
          ? plotWidth / 2
          : (index / (pointCount - 1)) * plotWidth
      );
      const yCash = (value) => plotHeight - ((Number(value) || 0) / cashMax) * plotHeight;
      const yWcr = (value) => plotHeight - ((Number(value) || 0) / wcrMax) * plotHeight;

      const cashPoints = cashTrend.length
        ? cashTrend.map((point, index) => `${xAt(index)},${yCash(point.value)}`).join(' ')
        : '';
      const wcrPoints = wcrTrend.length
        ? wcrTrend.map((point, index) => `${xAt(index)},${yWcr(point.value)}`).join(' ')
        : '';
      const cashPath = cashTrend.length
        ? this.buildLinePath(cashTrend.map((point, index) => ({
          x: xAt(index),
          y: yCash(point.value)
        })))
        : '';
      const wcrPath = wcrTrend.length
        ? this.buildLinePath(wcrTrend.map((point, index) => ({
          x: xAt(index),
          y: yWcr(point.value)
        })))
        : '';

      return {
        hasData: Boolean(cashPath || wcrPath),
        labels,
        cashPoints,
        wcrPoints,
        cashPath,
        wcrPath,
        plotWidth,
        plotHeight,
        cashYTicks: this.buildRatioAxisTicks(cashMax),
        wcrYTicks: this.buildRatioAxisTicks(wcrMax),
        yCash,
        yWcr
      };
    },
    payerConcentrationChart() {
      const trend = Array.isArray(this.data.payerConcentrationTrend)
        ? this.data.payerConcentrationTrend
        : [];

      return this.buildHorizontalBarChart(trend, 'percent');
    },
    agingBucketChart() {
      const trend = Array.isArray(this.data.agingBucketTrend)
        ? this.data.agingBucketTrend
        : [];

      return this.buildHorizontalBarChart(trend, 'currency');
    },
    recOutstandingChart() {
      const trend = Array.isArray(this.data.recOutstandingTrend)
        ? this.data.recOutstandingTrend
        : [];

      return this.buildHorizontalBarChart(trend, 'percent');
    },
    primePayerMetricRows() {
      return this.buildPayerMetricRows(this.data.primePayerStats, {
        adtpLabel: 'Weighted Average Days to Pay (ADTP)',
        pcrRecentAsterisk: Boolean(this.data.primePayerPcrFootnote)
      });
    },
    kemaApprovedPayerMetricRows() {
      return this.buildPayerMetricRows(this.data.kemaApprovedPayerStats, {
        adtpLabel: 'Weighted ADTP'
      });
    },
    riskScoringRows() {
      const summary = this.data.riskSummary || {};

      return RISK_SUMMARY_CATEGORIES.map(({ key, label }) => {
        const entry = summary[key] || {};

        return {
          label,
          scoreDisplay: this.formatRiskScoreDisplay(entry.score, entry.total)
        };
      });
    },
    riskOverallScoreDisplay() {
      const grandTotal = this.data.riskSummary?.['Grand Total'];

      if (!grandTotal) {
        return this.displayValue(this.data.riskScore);
      }

      const score = Number(grandTotal.score);

      if (!Number.isFinite(score)) return '—';

      const formatted = Number.isInteger(score) ? String(score) : score.toFixed(1);

      return `${formatted}/100`;
    },
    riskScoreGauge() {
      const grandTotal = this.data.riskSummary?.['Grand Total'];
      let score = grandTotal?.score;

      if (score === undefined || score === null || score === '') {
        score = this.data.riskScore;
      }

      const numeric = Number(score);

      if (!Number.isFinite(numeric)) {
        return { hasScore: false };
      }

      const clamped = Math.min(Math.max(numeric, 0), 100);
      const displayScore = Number.isInteger(clamped)
        ? String(clamped)
        : clamped.toFixed(1);

      return {
        hasScore: true,
        score: clamped,
        displayScore,
        markerLeftPct: clamped,
        ticks: [0, 50, 75, 100]
      };
    },
    riskPerformanceChart() {
      const summary = this.data.riskSummary || {};
      const bars = RISK_SUMMARY_CATEGORIES.map(({ key, label }) => {
        const entry = summary[key] || {};
        const pct = Number(entry.pct);
        const value = Number.isFinite(pct) ? pct * 100 : 0;

        return {
          label,
          value,
          widthPct: value
        };
      });

      const hasSummaryData = RISK_SUMMARY_CATEGORIES.some(({ key }) => summary[key]);

      if (!hasSummaryData) {
        return { hasData: false };
      }

      const xMax = 100;
      const xTicks = [0, 25, 50, 75, 100];

      return {
        hasData: true,
        xMax,
        xTicks,
        tickPosition(tick) {
          return Number(tick);
        },
        bars
      };
    }
  },
  methods: {
    pageVisible(pageNumber) {
      if (this.downloadingPdf || !this.singlePageView) {
        return true;
      }

      return this.visiblePage === pageNumber;
    },
    formatRiskScoreDisplay(score, total) {
      const scoreValue = Number(score);
      const totalValue = Number(total);

      if (!Number.isFinite(scoreValue) || !Number.isFinite(totalValue)) {
        return '—';
      }

      return `${scoreValue.toFixed(2)} / ${totalValue.toFixed(2)}`;
    },
    buildPayerMetricRows(stats, options = {}) {
      const values = stats || {};
      const adtpLabel = options.adtpLabel || 'Weighted Average Days to Pay (ADTP)';
      const pcrRecentAsterisk = Boolean(options.pcrRecentAsterisk);

      return [
        {
          label: 'Average Length of Relationship',
          recent: this.formatLorDisplay(values.lor_recent),
          lifetime: this.formatLorDisplay(values.lor_lifetime)
        },
        {
          label: 'Average Payment Completion Ratio',
          recent: this.formatPercentDisplay(values.pcr_recent, pcrRecentAsterisk),
          lifetime: this.formatPercentDisplay(values.pcr_lifetime)
        },
        {
          label: 'Average <=60 Days Payment Completion Ratio',
          recent: this.formatPercentDisplay(values.six_dpr_recent),
          lifetime: this.formatPercentDisplay(values.six_dpr_lifetime)
        },
        {
          label: adtpLabel,
          recent: this.formatDaysDisplay(values.adtp_recent),
          lifetime: this.formatDaysDisplay(values.adtp_lifetime)
        }
      ];
    },
    formatLorDisplay(value) {
      if (value === undefined || value === null || value === '') return '—';
      const normalized = String(value).trim();
      if (!normalized || normalized === '—') return '—';
      if (normalized.toUpperCase() === 'NA') return 'NA';
      if (/months?$/i.test(normalized)) return normalized;
      return `${normalized} Months`;
    },
    formatPercentDisplay(value, withAsterisk = false) {
      if (value === undefined || value === null || value === '') return '—';
      const normalized = String(value).trim();
      if (!normalized || normalized === '—') return '—';
      const formatted = normalized.includes('%') ? normalized : `${normalized}%`;
      return withAsterisk ? `${formatted}*` : formatted;
    },
    formatDaysDisplay(value) {
      if (value === undefined || value === null || value === '') return '—';
      const normalized = String(value).trim();
      if (!normalized || normalized === '—') return '—';
      if (/days?$/i.test(normalized)) return normalized;
      return `${normalized} Days`;
    },
    buildRatioAxisMax(values, fallback = 1) {
      const numericValues = (Array.isArray(values) ? values : [])
        .map((value) => Number(value))
        .filter((value) => Number.isFinite(value));

      const maxValue = numericValues.length ? Math.max(...numericValues, 0) : 0;

      if (maxValue <= 0) return fallback;

      const padded = maxValue * 1.08;
      const magnitude = 10 ** Math.floor(Math.log10(padded));
      const normalized = padded / magnitude;

      let nice;
      if (normalized <= 1) nice = 1;
      else if (normalized <= 2) nice = 2;
      else if (normalized <= 2.5) nice = 2.5;
      else if (normalized <= 5) nice = 5;
      else nice = 10;

      return nice * magnitude;
    },
    buildRatioAxisTicks(max, divisions = 4) {
      const ticks = [];

      for (let index = divisions; index >= 0; index -= 1) {
        const tick = (max / divisions) * index;
        ticks.push(Math.round(tick * 100) / 100);
      }

      return ticks;
    },
    buildHorizontalBarChart(trend, scaleType = 'percent') {
      if (!Array.isArray(trend) || !trend.length) {
        return { hasData: false };
      }

      const values = trend.map((item) => Number(item.value) || 0);
      const maxValue = Math.max(...values, 0);
      let xMax;

      if (scaleType === 'currency') {
        xMax = Math.max(Math.ceil(maxValue / 500000) * 500000, 500000);
      } else {
        xMax = Math.max(Math.ceil(maxValue / 5) * 5, 5);
      }

      const step = xMax / 4;
      const xTicks = [0, step, step * 2, step * 3, xMax];

      return {
        hasData: true,
        scaleType,
        xMax,
        xTicks,
        tickPosition(tick) {
          return (Number(tick) / xMax) * 100;
        },
        bars: trend.map((item) => {
          const value = Number(item.value) || 0;

          return {
            label: item.label || '—',
            sector: item.sector || '',
            value,
            widthPct: xMax ? (value / xMax) * 100 : 0
          };
        })
      };
    },
    buildLinePath(points) {
      if (!Array.isArray(points) || !points.length) return '';

      return points.map((point, index) => {
        const command = index === 0 ? 'M' : 'L';
        return `${command} ${point.x} ${point.y}`;
      }).join(' ');
    },
    async downloadPdf() {
      if (this.downloadingPdf || !this.$refs.pagesContainer) return;

      this.downloadingPdf = true;

      try {
        await this.$nextTick();
        const pageElements = this.$refs.pagesContainer.querySelectorAll('.report-page');

        if (!pageElements.length) {
          throw new Error('No report pages found.');
        }

        const pdf = new jsPDF('p', 'in', 'letter');

        for (let index = 0; index < pageElements.length; index += 1) {
          const pageElement = pageElements[index];
          await this.waitForImages(pageElement);

          const canvas = await html2canvas(pageElement, {
            scale: 2,
            useCORS: true,
            allowTaint: false,
            backgroundColor: '#ffffff',
            width: PAGE_WIDTH_PX,
            height: PAGE_HEIGHT_PX,
            windowWidth: PAGE_WIDTH_PX,
            scrollX: 0,
            scrollY: 0,
            logging: false,
            onclone: (documentClone) => {
              this.preparePageClone(documentClone, index);
            }
          });

          const imgData = canvas.toDataURL('image/jpeg', 0.98);
          const renderWidth = PDF_WIDTH_IN;
          const renderHeight = (canvas.height / canvas.width) * renderWidth;

          if (index > 0) {
            pdf.addPage('letter', 'p');
          }

          if (renderHeight > PDF_HEIGHT_IN) {
            pdf.addImage(imgData, 'JPEG', 0, 0, renderWidth, PDF_HEIGHT_IN);
          } else {
            pdf.addImage(imgData, 'JPEG', 0, 0, renderWidth, renderHeight);
          }
        }

        pdf.save(this.pdfFilename);
        this.$bvToast.toast('Credit assessment report downloaded successfully.', {
          title: 'Success',
          variant: 'success',
          solid: true,
          autoHideDelay: 3000
        });
      } catch (error) {
        console.error('Error generating preview PDF:', error);
        this.$bvToast.toast(
          'Unable to generate PDF. Please try again.',
          {
            title: 'Download failed',
            variant: 'danger',
            solid: true,
            autoHideDelay: 5000
          }
        );
      } finally {
        this.downloadingPdf = false;
      }
    },
    preparePageClone(documentClone, pageIndex) {
      const previewRoot = documentClone.querySelector('.credit-assessment-report-preview');
      if (previewRoot) {
        previewRoot.classList.add('is-exporting');
      }

      const pages = documentClone.querySelectorAll('.report-page');
      const clonedPage = pages[pageIndex];

      if (!clonedPage) return;

      clonedPage.style.width = `${PAGE_WIDTH_PX}px`;
      clonedPage.style.minHeight = `${PAGE_HEIGHT_PX}px`;
      clonedPage.style.height = `${PAGE_HEIGHT_PX}px`;
      clonedPage.style.boxShadow = 'none';
      clonedPage.style.border = 'none';
      clonedPage.style.margin = '0';
      clonedPage.style.overflow = 'hidden';

      this.fixChartExportStyles(clonedPage);
    },
    fixChartExportStyles(root) {
      root.querySelectorAll('.ratio-chart-svg, .yearly-sales-trend-svg').forEach((svg) => {
        svg.setAttribute('shape-rendering', 'geometricPrecision');
      });

      root.querySelectorAll('.payer-chart-y-label').forEach((label) => {
        label.style.display = 'block';
        label.style.position = 'static';
        label.style.whiteSpace = 'normal';
        label.style.wordBreak = 'break-word';
      });

      root.querySelectorAll('.payer-chart-row').forEach((row) => {
        row.style.display = 'grid';
      });

      root.querySelectorAll('.payer-chart-x-axis-row').forEach((row) => {
        row.style.display = 'grid';
      });
    },
    waitForImages(container) {
      const images = Array.from(container.querySelectorAll('img'));

      return Promise.all(images.map((image) => {
        if (image.complete) return Promise.resolve();

        return new Promise((resolve) => {
          image.onload = resolve;
          image.onerror = resolve;
        });
      }));
    },
    displayValue(value) {
      if (value === undefined || value === null || value === '') return '—';
      return String(value);
    },
    formatRichText(value) {
      if (!value) return '—';
      return String(value)
        .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
        .replace(/\n/g, '<br />');
    },
    formatChartTick(value) {
      const number = Number(value);
      if (Number.isNaN(number)) return '0';
      return number.toLocaleString('en-US');
    },
    formatRatioTick(value) {
      const number = Number(value);
      if (Number.isNaN(number)) return '0';
      if (Number.isInteger(number)) return String(number);
      return number.toFixed(1);
    },
    formatPercentTick(value) {
      const number = Number(value);
      if (Number.isNaN(number)) return '0.00%';
      return `${number.toFixed(2)}%`;
    },
    riskScoreTickStyle(tick) {
      const value = Number(tick);
      const base = { position: 'absolute', top: '0' };

      if (!Number.isFinite(value) || value <= 0) {
        return { ...base, left: '0', transform: 'none' };
      }

      if (value >= 100) {
        return { ...base, left: '100%', transform: 'translateX(-100%)' };
      }

      return {
        ...base,
        left: `${value}%`,
        transform: 'translateX(-50%)'
      };
    }
  }
};
</script>

<style lang="scss" scoped>
.credit-assessment-report-preview {
  margin-top: 2rem;
  padding-top: 1.5rem;
  border-top: 1px solid #e2e8f0;

  &.is-embedded {
    margin-top: 0;
    padding-top: 0;
    border-top: none;
  }

  &.is-single-page-view {
    .pdf-export-content {
      display: flex;
      justify-content: center;
    }

    .preview-header {
      margin-bottom: 1rem;
    }
  }
}

.preview-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  margin-bottom: 1.5rem;
  flex-wrap: wrap;
}

.preview-section-title {
  margin: 0;
  font-size: 1.05rem;
  font-weight: 600;
  color: #2d3748;
}

.preview-actions {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  flex-wrap: wrap;
}

.preview-action-btn {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 0.875rem;
  border-radius: 6px;
  font-size: 0.875rem;
  font-weight: 600;
  cursor: pointer;

  &:disabled {
    opacity: 0.7;
    cursor: not-allowed;
  }
}

.preview-action-btn-primary {
  border: none;
  background: #4f81bd;
  color: #ffffff;

  &:hover:not(:disabled) {
    background: #3d6a9e;
  }
}

.pdf-export-content {
  display: flex;
  flex-direction: column;
  gap: 2rem;
}

.is-exporting .pdf-export-content {
  gap: 0;
}

.report-page {
  position: relative;
  width: 816px;
  min-height: 1056px;
  margin: 0 auto;
  background: #ffffff;
  border: 1px solid #d1d5db;
  box-shadow: 0 8px 24px rgba(15, 23, 42, 0.08);
  padding: 28px 34px 56px;
  box-sizing: border-box;
  font-family: Arial, Helvetica, sans-serif;
  color: #111827;
}

.report-page:not(.report-page-cover):not(.report-page-executive-summary) {
  display: flex;
  flex-direction: column;
}

.report-page:not(.report-page-cover):not(.report-page-executive-summary) > .business-profile-panel,
.report-page:not(.report-page-cover):not(.report-page-executive-summary) > .yearly-sales-panel,
.report-page:not(.report-page-cover):not(.report-page-executive-summary) > .financial-analysis-panel,
.report-page:not(.report-page-cover):not(.report-page-executive-summary) > .loans-liabilities-panel,
.report-page:not(.report-page-cover):not(.report-page-executive-summary) > .payers-analysis-panel,
.report-page:not(.report-page-cover):not(.report-page-executive-summary) > .receivables-page10-panel,
.report-page:not(.report-page-cover):not(.report-page-executive-summary) > .risk-scoring-panel,
.report-page:not(.report-page-cover):not(.report-page-executive-summary) > .conclusion-panel,
.report-page:not(.report-page-cover):not(.report-page-executive-summary) > .appendix-panel {
  flex: 1 1 auto;
}

.is-exporting .report-page {
  box-shadow: none;
  border: none;
}

.report-page-cover {
  display: flex;
  flex-direction: column;
  justify-content: space-between;
}

.report-page-mini-header {
  font-size: 11px;
  color: #6b7280;
}

.report-page-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-shrink: 0;
  margin-bottom: 18px;
}

.report-page-header-label {
  font-size: 11px;
  color: #6b7280;
}

.logo-img {
  height: 22px;
}

.cover-body {
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  text-align: center;
  padding: 120px 0;
}

.cover-title {
  margin: 0 0 28px;
  font-size: 34px;
  font-weight: 700;
  line-height: 1.2;
}

.cover-subtitle {
  margin: 0;
  font-size: 24px;
  font-weight: 700;
}

.cover-merchant {
  display: inline-block;
}

.report-banner,
.report-section-banner {
  background: #2874E9;
  color: #ffffff;
  text-align: center;
  font-weight: 700;
  letter-spacing: 0.02em;
  padding: 8px 12px;
  margin-bottom: 14px;
  font-size: 15px;
}

.report-banner-left {
  text-align: left;
}

.report-section-banner {
  margin-top: 18px;
}

.report-info-table {
  width: 100%;
  border-collapse: separate;
  border-spacing: 0;
  border-top: 1px solid #111827;
  border-left: 1px solid #111827;
  table-layout: fixed;
  font-size: 12px;
  margin-bottom: 14px;

  th,
  td {
    border: none;
    border-right: 1px solid #111827;
    border-bottom: 1px solid #111827;
    padding: 6px 8px;
    vertical-align: top;
  }

  th {
    background: #f3f4f6;
    font-weight: 700;
    text-align: left;
  }
}

.report-info-table:not(.report-highlights-table):not(.report-risk-table):not(.report-recommendation-table):not(.report-requirement-table):not(.report-summary-panel-table):not(.business-profile-sales-table) {
  tbody td:nth-child(odd) {
    width: 22%;
  }
}

.label-cell {
  font-weight: 700;
}

.report-subheading {
  margin: 0 0 8px;
  font-size: 13px;
  font-weight: 700;
}

.report-paragraph {
  margin: 0 0 14px;
  font-size: 12px;
  line-height: 1.45;
  text-align: justify;
}

.report-summary-panel {
  border: 1px solid #111827;
  padding: 0;
  margin-bottom: 14px;
  overflow: hidden;

  .report-subheading,
  .report-paragraph {
    padding: 12px 14px 0;
  }

  .report-summary-panel-table {
    width: 100%;
    margin: 0;
    border: none;
    border-top: 1px solid #111827;
    border-left: none;
    border-collapse: collapse;
    table-layout: fixed;
    margin-bottom: 0;
    max-width: none;

    td {
      border: none;
      border-bottom: 1px solid #111827;
      padding: 7px 10px;
      vertical-align: middle;
      font-weight: 400;
    }

    td:first-child {
      width: 58%;
      border-right: 1px solid #111827;
    }

    td.label-cell {
      font-weight: 700;

      em,
      i {
        font-weight: 400;
        font-style: italic;
      }
    }

    tr:last-child td {
      border-bottom: none;
    }
  }
}

.report-requirement-panel {
  .report-paragraph {
    margin-bottom: 12px;
  }
}

.report-highlights-panel {
  .report-subheading {
    margin-bottom: 12px;
  }
}

.report-footnotes {
  margin-top: 18px;
  font-size: 10px;
  color: #374151;

  p {
    margin: 0 0 4px;
  }
}


.report-risk-table {
  margin-top: 8px;
}

.risk-highlight-cell {
  background: #ffff00;
  min-height: 48px;
  font-size: 12px;
  color: #111827;
}

.risk-score-value-cell {
  padding: 8px 10px;
  vertical-align: middle;
}

.risk-grade-cell {
  font-weight: 700;
  text-align: center;
  vertical-align: middle;
}

.risk-score-gauge {
  width: 100%;
  padding: 4px 4px 2px;
  box-sizing: border-box;
}

.risk-score-gauge-ticks {
  position: relative;
  width: 100%;
  height: 14px;
  margin-bottom: 4px;
}

.risk-score-gauge-tick-label {
  position: absolute;
  top: 0;
  font-size: 11px;
  font-weight: 700;
  line-height: 1;
  color: #111827;
  white-space: nowrap;
}

.risk-score-gauge-scale {
  position: relative;
  width: 100%;
  min-height: 32px;
  padding-top: 6px;
  padding-bottom: 22px;
  border-top: 1px solid #111827;
  box-sizing: border-box;
}

.risk-score-gauge-marker {
  position: absolute;
  top: 6px;
  transform: translateX(-50%);
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  white-space: nowrap;
}

.risk-score-gauge-marker-arrow {
  font-size: 10px;
  line-height: 1;
  color: #111827;
}

.risk-score-gauge-marker-value {
  margin-top: 2px;
  font-size: 12px;
  font-weight: 700;
  line-height: 1.1;
  color: #111827;
}

.report-strengths-weaknesses {
  display: flex;
  flex-direction: column;
  margin: 14px 0;
  border: 1px solid #111827;
}

.sw-block {
  min-height: auto;
}

.sw-block + .sw-block {
  border-top: 1px solid #111827;
}

.sw-header {
  padding: 6px 8px;
  font-size: 12px;
  font-weight: 700;
  border-bottom: 1px solid #111827;
}

.sw-header-strengths {
  background: #c5e0b4;
}

.sw-header-weaknesses {
  background: #f8cbad;
}

.sw-list {
  margin: 0;
  padding: 8px 18px 12px 24px;
  font-size: 11px;
  line-height: 1.45;

  li {
    margin-bottom: 8px;
  }
}

.sw-empty {
  list-style: none;
  margin-left: -16px;
}

.report-recommendation-table .recommendation-value-cell {
  font-weight: 700;
  text-align: center;
  color: #ffffff;
  text-transform: uppercase;
}

.report-recommendation-table .approved-cell {
  background: #92d050;
}

.report-recommendation-table .rejected-cell {
  background: #dc2626;
}

.cell-subtext {
  display: inline;
  font-size: 10px;
  font-style: italic;
  font-weight: 400;
  margin-left: 4px;
}

.report-page-footer {
  position: absolute;
  left: 34px;
  right: 34px;
  bottom: 22px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-top: 8px;
  border-top: 2px solid #7f1d1d;
  font-size: 11px;
  color: #6b7280;
}

.business-profile-panel {
  border: 1px solid #111827;
  padding: 0;
  overflow: hidden;

  .report-subheading,
  .report-paragraph {
    padding: 12px 14px 0;
  }

  .report-paragraph {
    margin-bottom: 12px;
  }

  .business-profile-sales-table {
    width: 100%;
    margin: 0;
    border: none;
    border-top: 1px solid #111827;
    border-left: none;
    border-collapse: collapse;
    table-layout: fixed;

    td {
      border: none;
      border-right: 1px solid #111827;
      border-bottom: 1px solid #111827;
      padding: 7px 10px;
      vertical-align: middle;
      width: 25%;
    }

    td:last-child {
      border-right: none;
    }
  }

  .sales-trend-heading {
    margin: 12px 0 8px;
    padding: 0 14px;
  }

  .sales-chart,
  .sales-chart-empty {
    margin-left: 14px;
    margin-right: 14px;
  }

  .sales-metrics-list {
    padding: 0 14px;
  }

  .sales-analysis-list {
    padding: 8px 14px 12px 28px;
  }
}

.sales-chart {
  display: flex;
  gap: 8px;
  margin-bottom: 16px;
  min-height: 220px;
}

.sales-chart-y-axis {
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  width: 72px;
  height: 168px;
  margin-top: 12px;
  font-size: 10px;
  color: #374151;
  text-align: right;
}

.sales-chart-body {
  position: relative;
  flex: 1;
  min-width: 0;
}

.sales-chart-plot {
  position: relative;
  height: 180px;
  padding-top: 12px;
  box-sizing: border-box;
}

.sales-chart-grid {
  position: absolute;
  top: 12px;
  right: 0;
  bottom: 0;
  left: 0;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  pointer-events: none;
}

.sales-chart-grid-line {
  border-top: 1px solid #d1d5db;

  &:last-child {
    border-top: none;
    border-bottom: 1px solid #d1d5db;
  }
}

.sales-chart-bars {
  position: absolute;
  top: 12px;
  right: 0;
  bottom: 0;
  left: 0;
  z-index: 1;
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 1.6px;
  padding: 0 1.6px;
}

.sales-bar-column {
  flex: 1;
  min-width: 0;
  height: 100%;
  display: flex;
  align-items: flex-end;
  justify-content: center;
}

.sales-bar-fill {
  width: 25px;
  background: #2874E9;
  min-height: 0;
}

.sales-chart-x-labels {
  display: flex;
  justify-content: space-between;
  gap: 1.6px;
  padding: 6px 1.6px 0;
}

.sales-bar-label {
  flex: 1;
  min-width: 0;
  font-size: 9px;
  color: #374151;
  text-align: center;
  line-height: 1.2;
  white-space: nowrap;
}

.sales-chart-empty {
  margin-bottom: 16px;
  font-size: 12px;
  color: #6b7280;
}

.report-page[data-report-page="5"],
.report-page[data-report-page="7"] {
  height: 1056px;
}

.yearly-sales-panel {
  display: flex;
  flex-direction: column;
  flex: 1 1 0;
  min-height: 0;
  width: 100%;
  border: 1px solid #111827;
  padding: 14px 16px 16px;
}

.yearly-sales-title {
  display: inline-block;
  margin: 0 0 16px;
  font-size: 13px;
  font-weight: 700;
  color: #111827;
}

.yearly-sales-chart {
  display: flex;
  flex-direction: column;
  flex: 0 0 auto;
  margin-top: 24px;
  margin-bottom: 18px;
}

.yearly-sales-plot-row {
  display: flex;
  gap: 10px;
  height: 210px;
}

.yearly-sales-y-axis {
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  width: 28px;
  flex-shrink: 0;
  font-size: 11px;
  color: #374151;
  text-align: right;
}

.yearly-sales-plot {
  position: relative;
  flex: 1;
  min-width: 0;
  height: 210px;
  box-sizing: border-box;
}

.yearly-sales-grid {
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  pointer-events: none;
}

.yearly-sales-grid-line {
  border-top: 1px solid #d1d5db;

  &:last-child {
    border-top: none;
    border-bottom: 1px solid #d1d5db;
  }
}

.yearly-sales-trend-svg {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  overflow: visible;
  z-index: 2;
  pointer-events: none;
}

.yearly-sales-trend-line {
  fill: none;
  stroke: #ed7d31;
  stroke-width: 2;
  stroke-linecap: round;
  stroke-linejoin: round;
  vector-effect: non-scaling-stroke;
}

.yearly-sales-bars {
  position: absolute;
  inset: 0;
  z-index: 1;
  display: flex;
  align-items: flex-end;
  justify-content: space-around;
}

.yearly-sales-column {
  flex: 1;
  height: 100%;
  display: flex;
  align-items: flex-end;
  justify-content: center;
}

.yearly-sales-bar-fill {
  position: relative;
  width: 92px;
  max-width: 70%;
  background: #2874E9;
  min-height: 0;
  display: flex;
  align-items: flex-start;
  justify-content: center;
  padding-top: 8px;
}

.yearly-sales-bar-value {
  font-size: 12px;
  font-weight: 700;
  color: #ffffff;
  line-height: 1;
}

.yearly-sales-x-labels {
  display: flex;
  align-items: flex-start;
  margin-top: 8px;
}

.yearly-sales-y-spacer {
  width: 28px;
  margin-right: 10px;
  flex-shrink: 0;
}

.yearly-sales-x-label {
  flex: 1;
  font-size: 11px;
  color: #374151;
  text-align: center;
  line-height: 1.3;
}

.yearly-sales-notes {
  margin-top: 8px;
}

.loans-liabilities-panel {
  display: flex;
  flex-direction: column;
  flex: 1 1 0;
  min-height: 0;
  width: 100%;
  border: 1px solid #111827;
  padding: 14px 16px 16px;
}

.loans-liabilities-title {
  display: inline-block;
  margin: 0;
  font-size: 13px;
  font-weight: 700;
  color: #111827;
}

.financial-analysis-panel {
  border: 1px solid #111827;
  padding: 12px 14px;
}

.financial-ratio-heading-spacer {
  height: 1rem;
  flex: 0 0 1rem;
}

.financial-analysis-list {
  margin-top: 14px;
}

.payers-analysis-panel {
  border: 1px solid #111827;
  padding: 12px 14px;
}

.payers-page8-overview-section {
  margin-top: 18px;
}

.payer-metrics-section {
  margin: 0;
}

.payer-metrics-section-spaced {
  margin-top: 16px;
}

.payer-metrics-text-box {
  border: 1px solid #111827;
  padding: 10px 12px;
  margin-bottom: 12px;
}

.payer-metrics-intro-list,
.payer-metrics-overview-list {
  margin: 0;
}

.payer-metrics-overview-heading {
  margin: 0 0 8px;
}

.payer-metrics-footnote {
  margin: 8px 0 0;
  font-size: 10px;
  font-style: italic;
  line-height: 1.4;
  color: #374151;
}

.payer-metrics-table {
  margin-bottom: 0;

  thead th {
    background: #4b5563;
    color: #ffffff;
  }

  tbody td:nth-child(odd) {
    width: 46%;
  }

  tbody td:nth-child(even),
  tbody td:nth-child(3) {
    width: 27%;
  }
}

.report-page-payer-metrics {
  .report-footnotes {
    margin-top: 12px;
  }
}

.risk-scoring-table {
  margin-bottom: 14px;

  thead th {
    background: #4b5563;
    color: #ffffff;
  }

  tbody td:nth-child(odd) {
    width: 58%;
  }
}

.risk-scoring-panel {
  border: 1px solid #111827;
  padding: 12px 14px;
}

.risk-performance-chart {
  margin: 8px 0 14px;
  padding-top: 6px;
}

.risk-scoring-strengths-heading {
  margin-top: 4px;
  margin-bottom: 8px;
}

.payers-analysis-list {
  margin-top: 14px;
}

.receivables-page10-panel {
  border: 1px solid #111827;
  padding: 12px 14px;
}

.receivables-page10-section {
  & + & {
    margin-top: 18px;
  }

  .report-subheading {
    margin-bottom: 6px;
  }

  .payers-analysis-list {
    margin-top: 10px;
  }
}

.payer-concentration-chart {
  margin: 10px 0 4px;
  padding-top: 10px;
}

.ratio-trend-chart {
  display: flex;
  gap: 4px;
  align-items: flex-start;
  margin: 0 0 8px;
}

.ratio-chart-y-axis-wrap {
  display: flex;
  align-items: center;
  flex-shrink: 0;
  margin-top: 12px;
  height: 150px;
}

.ratio-chart-y-axis-wrap-left {
  gap: 4px;
}

.ratio-chart-y-axis-wrap-right {
  gap: 4px;
}

.ratio-axis-title-stack {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 1px;
  width: 16px;
  font-size: 9px;
  font-weight: 700;
  color: #111827;
  line-height: 1.05;
  text-align: center;
  white-space: nowrap;
}

.ratio-chart-y-axis {
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  height: 100%;
  font-size: 9px;
  color: #374151;
  flex-shrink: 0;
}

.ratio-chart-y-axis-left {
  width: 14px;
  text-align: right;
}

.ratio-chart-y-axis-right {
  width: 14px;
  text-align: left;
}

.ratio-chart-main {
  flex: 1;
  min-width: 0;
}

.ratio-chart-svg {
  display: block;
  width: 100%;
  max-width: 620px;
  height: 150px;
  margin-top: 12px;
  overflow: visible;
}

.ratio-line {
  fill: none;
  stroke-width: 2.5;
  stroke-linecap: round;
  stroke-linejoin: round;
  vector-effect: non-scaling-stroke;
}

.ratio-line-wcr {
  stroke: #4f81bd;
}

.ratio-line-cash {
  stroke: #ed7d31;
}

.ratio-chart-grid-lines line {
  stroke: #d1d5db;
  stroke-width: 1;
  vector-effect: non-scaling-stroke;
}

.ratio-chart-x-labels {
  display: flex;
  justify-content: space-between;
  gap: 2px;
  margin-top: 4px;
}

.ratio-chart-x-label {
  flex: 1;
  min-width: 0;
  font-size: 9px;
  color: #374151;
  text-align: center;
  line-height: 1.2;
  white-space: nowrap;
}

.ratio-chart-legend {
  display: flex;
  justify-content: center;
  gap: 18px;
  margin-top: 8px;
  font-size: 10px;
  color: #374151;
}

.ratio-legend-item {
  display: inline-flex;
  align-items: center;
  gap: 6px;
}

.ratio-legend-swatch {
  width: 18px;
  height: 3px;
  border-radius: 1px;
}

.ratio-legend-swatch-wcr {
  background: #4f81bd;
}

.ratio-legend-swatch-cash {
  background: #ed7d31;
}

.sales-metrics-list {
  display: flex;
  flex-direction: column;
  align-items: center;
  margin: 0 0 12px;
  padding: 0;
  list-style: none;
  font-size: 11px;
  line-height: 1.5;
}

.sales-metric-item {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  margin-bottom: 6px;
}

.sales-metric-check {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 14px;
  height: 14px;
  margin-top: 1px;
  background: #2874E9;
  color: #ffffff;
  font-size: 10px;
  font-weight: 700;
  flex-shrink: 0;
}

.sales-analysis-list {
  margin: 0;
  font-size: 11px;
  line-height: 1.45;
}

.appendix-panel {
  border: 1px solid #111827;
  padding: 14px 18px;
}

.conclusion-panel {
  border: 1px solid #111827;
  padding: 14px 18px;
}

.conclusion-section-heading {
  margin: 0 0 8px;
  font-size: 12px;
  font-weight: 700;
  color: #111827;

  &:not(:first-child) {
    margin-top: 16px;
  }
}

.conclusion-list {
  margin: 0;
  padding-left: 22px;
  font-size: 11px;
  line-height: 1.5;
  color: #111827;

  li {
    margin-bottom: 10px;

    &:last-child {
      margin-bottom: 0;
    }
  }
}

.conclusion-empty {
  list-style: none;
  margin-left: -16px;
}

.appendix-list {
  margin: 0;
  padding-left: 22px;
  font-size: 12px;
  line-height: 1.55;
  color: #111827;

  li {
    margin-bottom: 10px;

    &:last-child {
      margin-bottom: 0;
    }
  }
}

.appendix-metrics-table {
  thead th {
    background: #ffffff;
    font-weight: 700;
  }

  tbody td:first-child {
    width: 34%;
    vertical-align: top;
  }

  tbody td:last-child {
    vertical-align: top;
  }
}

.prime-payers-table {
  thead th {
    background: #ffffff;
    font-weight: 700;
    text-align: left;
  }

  tbody td {
    font-size: 11px;
    line-height: 1.35;
    vertical-align: top;
  }

  tbody td:first-child {
    width: 38%;
  }

  tbody td:last-child {
    width: 38%;
  }
}

.prime-payers-rating-header,
.prime-payers-rating-cell {
  width: 16%;
  text-align: center;
}

.debts-table {
  thead th {
    background: #ffffff;
    font-weight: 700;
    text-align: left;
    font-size: 11px;
  }

  tbody td {
    font-size: 11px;
    line-height: 1.35;
    vertical-align: top;
  }

  tbody td:first-child {
    width: 44%;
  }

  tbody td:nth-child(2),
  tbody td:nth-child(3) {
    text-align: right;
  }
}

@media (max-width: 900px) {
  .report-page {
    width: 100%;
    min-height: auto;
    padding-bottom: 72px;
  }
}

.is-exporting .report-page {
  width: 816px !important;
  min-height: 1056px !important;
}
</style>
